import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getStaffEntitlements } from './api';
import { sharedMembershipKeys } from './keys';
import { mergeScheduleMembershipEntitlements } from './scheduleEntitlements';

type SelectedScheduleMember = {
    value?: string;
    memberships?: any[];
    [key: string]: unknown;
};

export const useScheduleMembershipEntitlements = ({
    selectedMembers,
    centerId,
    coachId,
    enabled,
}: {
    selectedMembers: SelectedScheduleMember[];
    centerId: string;
    coachId: string;
    enabled: boolean;
}) => {
    const members = Array.isArray(selectedMembers) ? selectedMembers : [];
    const canQuery =
        enabled === true && centerId.trim().length > 0 && coachId.trim().length > 0;
    const results = useQueries({
        queries: members.map((member) => {
            const memberId = String(member?.value || '').trim();
            return {
                // coachId 없이 조회한다(프로필과 같은 호출·같은 캐시). 서버에 coachId를
                // 넘기면 다른 강사 담당 공유 수강권이 응답에서 통째로 빠져, 화면이
                // "왜 안 뜨는지"조차 셀 수 없다. 강사 필터는 아래에서 직접 걸고
                // 제외 수를 안내에 쓴다.
                queryKey: sharedMembershipKeys.staffEntitlements(centerId, '', memberId),
                // 회원 프로필의 훅들과 같은 키·같은 요청을 공유한다 (중복 호출 제거).
                queryFn: () => getStaffEntitlements({ memberId, centerId }),
                enabled: canQuery && memberId.length > 0,
                staleTime: 30_000,
                retry: 1,
            };
        }),
    });

    const entitlementResultVersion = results
        .map((result) => `${result.status}:${result.dataUpdatedAt}`)
        .join('|');
    // 공유 수강권은 풀의 담당 강사 수업에서만 쓸 수 있다. 다른 강사를 고른 화면에는
    // 옵션으로 올리지 않되, "빠졌다"는 사실은 제외 수로 남겨 안내에 쓴다.
    const normalizedCoachId = coachId.trim();
    const selectedMembersWithEntitlements: any[] = useMemo(
        () =>
            members.map((member, index) => {
                const result = results[index];
                if (!result?.isSuccess) return member;
                const usableEntitlements = (result.data?.entitlements || []).filter(
                    (entitlement) =>
                        entitlement.access.type !== 'SHARED' ||
                        !normalizedCoachId ||
                        String(entitlement.membership.coachId || '') === normalizedCoachId,
                );
                return {
                    ...member,
                    memberships: mergeScheduleMembershipEntitlements(
                        Array.isArray(member.memberships) ? member.memberships : [],
                        usableEntitlements,
                    ),
                };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [members, normalizedCoachId, entitlementResultVersion],
    );

    /**
     * 목록에서 빠진 공유 수강권 수. 빠지는 이유는 하나뿐이다 —
     * 공유 풀의 담당 강사가 아닌 강사를 선택한 화면.
     * 이 값이 없으면 화면은 "수강권이 없는 회원"으로 잘못 안내한다 — 실제로는 있는데
     * 이 강사로 쓸 수 없을 뿐이다.
     */
    const excludedSharedMembershipCounts: number[] = useMemo(
        () =>
            members.map((_member, index) => {
                const result = results[index];
                if (!result?.isSuccess) return 0;
                const shared = (result.data?.entitlements || []).filter(
                    (entitlement) => entitlement.access.type === 'SHARED',
                );
                if (!normalizedCoachId) return 0;
                return shared.filter(
                    (entitlement) =>
                        String(entitlement.membership.coachId || '') !== normalizedCoachId,
                ).length;
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [members, normalizedCoachId, entitlementResultVersion],
    );

    const hasSelectedMembers = members.length > 0;
    const isReady =
        !enabled ||
        !hasSelectedMembers ||
        (canQuery && results.length === members.length && results.every((result) => result.isSuccess));

    // retry:1 이 소진되면 쿼리는 에러 상태로 굳고 스스로 다시 조회하지 않는다.
    // 화면이 "다시 시도해주세요"라고 안내하려면 실제 재조회 수단이 있어야 한다.
    const refetchFns = results.map((result) => result.refetch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const refetch = useMemo(() => () => refetchFns.forEach((fn) => void fn?.()), [entitlementResultVersion]);

    return {
        selectedMembers: selectedMembersWithEntitlements,
        excludedSharedMembershipCounts,
        isReady,
        isLoading: enabled && hasSelectedMembers && results.some((result) => result.isLoading),
        isError: enabled && hasSelectedMembers && results.some((result) => result.isError),
        refetch,
    };
};
